import React, { memo, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import sanitizeHtml from 'sanitize-html';
import TextWithLinks from 'components/TextWithLinks';

const EmailIframe = memo(({ id, html }) => {
    const isIframeAttached = useRef(false);
    const iframeContainerRef = useRef(null);

    const isHtml = useMemo(() => {
        let finalHtml = html;
        const doctypeMatch = finalHtml.match(/<!DOCTYPE[^>[]*(\[[^]]*\])?>/gm);
        if (doctypeMatch) {
            finalHtml = finalHtml.replace(/<!DOCTYPE[^>[]*(\[[^]]*\])?>/, '');
        }
        return /^<html[^>]*>[\s\S]*<\/html>/gi.test(finalHtml);
    }, [html]);

    const sanitize = useCallback((html) => {
        return sanitizeHtml(html, {
            allowedTags: sanitizeHtml.defaults.allowedTags.concat([
                'html',
                'head',
                'body',
                'img',
                'button',
            ]),
            allowedAttributes: {
                '*': [
                    'href',
                    'name',
                    'target',
                    'src',
                    'srcset',
                    'alt',
                    'title',
                    'width',
                    'height',
                    'loading',
                    'style',
                    'align',
                ],
            },
            allowedSchemes: ['data', 'http', 'https', 'cid'],
            enforceHtmlBoundary: false,
        });
    }, []);

    const correctFontFamilyQuotes = useCallback((html) => {
        if (!html) return '';
        let fontFamilyMatches = html.match(/font-family:.+?(?=;)/gm);
        const uniqFontFamilyMatches = [...new Set(fontFamilyMatches)];
        let newHtml = html;
        uniqFontFamilyMatches.forEach((current) => {
            // sorry :(
            if (current.includes('>')) {
                return;
            }
            let newCurrent = '';
            newCurrent = current.replace(/\"/g, "'");
            // we add also sans-serif as a backup font
            if (!newCurrent.includes('sans-serif')) newCurrent = `${newCurrent}, sans-serif`;
            newHtml = newHtml.replace(new RegExp(`${current}`, 'g'), `${newCurrent}`);
        });
        return newHtml;
    }, []);

    const setBodyFontFamily = useCallback((html) => {
        let finalHtml = html;
        let bodyMatch = html.match(/<body.+?(?=>)/gm);
        if (!bodyMatch?.[0]) {
            finalHtml = finalHtml.replace('<body>', '<body style="font-family: sans-serif">');
            return finalHtml;
        }
        let finalBodyTag = bodyMatch[0];

        let styleMatch = bodyMatch[0].match(/style=".+?(?=")/gm);
        if (!styleMatch?.[0]) {
            finalHtml = finalHtml.replace(
                bodyMatch,
                `${bodyMatch} style="font-family: sans-serif"`,
            );
            return finalHtml;
        }

        let fontFamilyMatches = styleMatch[0].match(/font-family:.+?(?=;)/gm);
        const fontFamily = fontFamilyMatches?.[0] || '';

        if (fontFamily && !fontFamily.includes('sans-serif')) {
            finalBodyTag = finalBodyTag.replace(fontFamily, `${fontFamily}, sans-serif`);
        } else if (!fontFamily) {
            finalBodyTag = finalBodyTag.slice(0, -1);
            const hasLastSemicolon = finalBodyTag.charAt(finalBodyTag.length - 1) === ';';
            if (hasLastSemicolon) finalBodyTag = `${finalBodyTag} font-family: sans-serif;"`;
            else finalBodyTag = `${finalBodyTag}; font-family: sans-serif;"`;
        }

        finalHtml = html.replace(bodyMatch[0], finalBodyTag);
        return finalHtml;
    }, []);

    const setTargetForLinks = useCallback((html) => {
        let finalHtml = html;
        let linkMatch = html.match(/<a.+?(?=>)/gm);
        const targetRegEx = /target=".+?"/g;
        const emptyTargetRegEx = /target=""/g;

        finalHtml = html.replace(targetRegEx, 'target="_blank"');
        finalHtml = finalHtml.replace(emptyTargetRegEx, 'target="_blank"');
        if (!linkMatch?.length) return finalHtml;

        linkMatch.forEach((current) => {
            if (!linkMatch.includes('target="_blank"')) {
                let finalLinkMatch = `${current} target="_blank"`;
                finalHtml = finalHtml.replace(current, finalLinkMatch);
            }
        });

        return finalHtml;
    }, []);

    const normalizeTableLayout = useCallback((html) => {
        if (!html || typeof DOMParser === 'undefined') return html;

        try {
            const parser = new DOMParser();
            const documentNode = parser.parseFromString(html, 'text/html');

            documentNode.querySelectorAll('table').forEach((table) => {
                table.style.removeProperty('width');
                table.style.removeProperty('min-width');
                table.style.maxWidth = '100%';
                table.style.width = '100%';
            });

            documentNode.querySelectorAll('td, th').forEach((cell) => {
                cell.style.removeProperty('width');
                cell.style.removeProperty('min-width');
                cell.style.maxWidth = '100%';
            });

            documentNode.querySelectorAll('table *').forEach((element) => {
                const textContent = element.textContent?.trim() || '';
                const hasLongText = textContent.length > 30;
                const hasLongWord = /[^\s]{20,}/.test(textContent);

                if (element.style?.whiteSpace === 'nowrap' && (hasLongText || hasLongWord)) {
                    element.style.whiteSpace = 'normal';
                    element.style.overflowWrap = 'anywhere';
                    element.style.wordBreak = 'break-word';
                }
            });

            documentNode.querySelectorAll('td, th').forEach((cell) => {
                const textContent = cell.textContent?.trim() || '';
                const isNumericLike = /^[\d\s.,€$£%+-]+$/.test(textContent);
                const isShortToken = textContent.length > 0 && textContent.length <= 20;

                if (isNumericLike && isShortToken) {
                    cell.style.whiteSpace = 'nowrap';
                    cell.style.overflowWrap = 'normal';
                    cell.style.wordBreak = 'normal';

                    const firstChild = cell.firstElementChild;
                    if (firstChild?.style) {
                        firstChild.style.whiteSpace = 'nowrap';
                        firstChild.style.overflowWrap = 'normal';
                        firstChild.style.wordBreak = 'normal';
                    }
                }
            });

            documentNode.querySelectorAll('img').forEach((img) => {
                img.style.maxWidth = '100%';
                img.style.height = 'auto';
            });

            return documentNode.documentElement.outerHTML;
        } catch (error) {
            console.error(error);
            return html;
        }
    }, []);

    const cleanHtml = useMemo(() => {
        let finalHtml = html.replace(/target="_self"/g, 'target="_blank"');

        // Remove all script and style tags even when they are declared between CSS to prevent XSS
        finalHtml = finalHtml.replace(new RegExp(/<script>([\s\S]*?)<\/script>/gim), '');
        finalHtml = finalHtml.replace(new RegExp(/<style>([\s\S]*?)<\/style>/gim), '');
        // fixing possible quote inconsistencies in the incoming html string
        try {
            finalHtml = correctFontFamilyQuotes(finalHtml);
        } catch (e) {
            console.error(e);
        }

        // set a default font-family
        finalHtml = setBodyFontFamily(finalHtml);
        // make all links open as _blank
        finalHtml = setTargetForLinks(finalHtml);
        // avoid large email table cells forcing overflow in the page layout
        finalHtml = normalizeTableLayout(finalHtml);
        finalHtml = sanitize(finalHtml);

        return finalHtml;
    }, [
        html,
        correctFontFamilyQuotes,
        setBodyFontFamily,
        setTargetForLinks,
        normalizeTableLayout,
        sanitize,
    ]);

    const containerId = useMemo(() => {
        if (!id) return '';
        return `fm-email-iframe-container__${id}`;
    }, [id]);

    useEffect(() => {
        const iframeContainer = document.getElementById(containerId);
        if (
            !cleanHtml?.length ||
            isIframeAttached.current ||
            !iframeContainer ||
            !isHtml ||
            !iframeContainerRef?.current?.offsetWidth
        )
            return;

        const head = cleanHtml.match(/<head[^>]*>[\s\S]*<\/head>/gi);
        const bodyHtml = cleanHtml.match(/<body[^>]*>[\s\S]*<\/body>/gi);
        if (!head || !bodyHtml) {
            console.error('EmailIframe html has no head or body');
            return;
        }

        const iframe = document.createElement('iframe');
        iframe.setAttribute('scrolling', 'no');
        iframe.style.cssText =
            'width:100%;height:1px;display:block;border:0;overflow:hidden;background:transparent;';
        iframeContainer.appendChild(iframe);

        try {
            iframe.contentWindow.document.open();
            iframe.contentWindow.document.write(`${head[0]}${bodyHtml[0]}`);
            iframe.contentWindow.document.close();
        } catch (e) {
            console.error(e);
            iframe.remove();
            return;
        }

        const doc = iframe.contentWindow.document;
        const iframeBody = doc.body;
        iframeBody.style.margin = '0';
        iframeBody.style.padding = '0';

        // Marketing emails frequently force html/body to height:100%, height:100vh, or have tables
        // with height="100%". Combined with a sized iframe viewport, scrollHeight/offsetHeight then
        // track the iframe size instead of the actual content, which creates a feedback loop with
        // any external ResizeObserver and grows the layout forever. We wrap the email markup in a
        // dedicated content node and neutralize percentage-based heights on html/body so we can
        // measure the true content height regardless of the iframe viewport size.
        const contentRoot = doc.createElement('div');
        contentRoot.setAttribute('data-fm-email-content-root', '');
        while (iframeBody.firstChild) {
            contentRoot.appendChild(iframeBody.firstChild);
        }
        iframeBody.appendChild(contentRoot);

        const resetStyle = doc.createElement('style');
        resetStyle.textContent = `
            html, body {
                height: auto !important;
                min-height: 0 !important;
                max-height: none !important;
                max-width: 100%;
                overflow: hidden !important;
            }
            body { margin: 0 !important; padding: 0 !important; }
            [data-fm-email-content-root] {
                display: block;
                height: auto !important;
                min-height: 0 !important;
                max-height: none !important;
                max-width: 100%;
                overflow: visible;
                box-sizing: border-box;
            }
            [data-fm-email-content-root] table { height: auto !important; }
        `;
        doc.head.appendChild(resetStyle);

        const VERTICAL_PADDING = 24;
        const STABLE_LIMIT = 3;
        let lastWidth = 0;
        let lastHeight = 0;
        let stableHits = 0;
        let teardown = false;

        const computeContentHeight = () => {
            const rectHeight = Math.ceil(contentRoot.getBoundingClientRect().height);
            return Math.max(contentRoot.scrollHeight, contentRoot.offsetHeight, rectHeight);
        };

        const applyDimensions = () => {
            if (teardown) return;
            if (!iframeContainerRef.current || !iframe.contentWindow) return;

            const containerWidth = iframeContainerRef.current.offsetWidth;
            if (!containerWidth) return;

            const nextBodyWidth = Math.max(containerWidth - 10, 0);
            const widthChanged = nextBodyWidth !== lastWidth;

            if (widthChanged) {
                lastWidth = nextBodyWidth;
                stableHits = 0;
                iframeBody.style.width = `${nextBodyWidth}px`;
                iframeBody.style.overflowX = 'clip';
                contentRoot.style.width = `${nextBodyWidth}px`;
            }

            const contentHeight = computeContentHeight();
            const nextHeight = contentHeight + VERTICAL_PADDING;

            if (!widthChanged && nextHeight === lastHeight) {
                stableHits += 1;
                return;
            }

            lastHeight = nextHeight;
            stableHits = widthChanged ? 0 : 1;
            iframe.style.height = `${nextHeight}px`;
        };

        // Defer first measurement to next frame: doc.write content is parsed synchronously but
        // computed styles (font fallbacks, table reflow) are only stable after the next layout.
        let firstFrameId = window.requestAnimationFrame(() => {
            firstFrameId = window.requestAnimationFrame(() => {
                applyDimensions();
            });
        });

        isIframeAttached.current = true;

        const imageCleanupFns = [];
        contentRoot.querySelectorAll('img').forEach((img) => {
            if (img.complete) return;
            const onLoadOrError = () => {
                if (teardown) return;
                applyDimensions();
            };
            img.addEventListener('load', onLoadOrError, { once: true });
            img.addEventListener('error', onLoadOrError, { once: true });
            imageCleanupFns.push(() => {
                img.removeEventListener('load', onLoadOrError);
                img.removeEventListener('error', onLoadOrError);
            });
        });

        // Observe only the wrapper that React owns. The wrapper auto-sizes to the iframe height,
        // so the observer fires whenever the iframe grows; we ignore those callbacks via the
        // width-comparison and stability counter to avoid feedback loops with any ancestor that
        // changes width when a scrollbar appears.
        let pendingFrameId = null;
        let resizeObserver = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver((entries) => {
                if (teardown || !iframeContainerRef.current) return;
                const entry = entries[0];
                if (!entry) return;

                const observedWidth = Math.round(
                    entry.contentRect
                        ? entry.contentRect.width
                        : iframeContainerRef.current.offsetWidth,
                );
                const expectedWidth = Math.max(observedWidth - 10, 0);

                if (expectedWidth === lastWidth) {
                    if (stableHits >= STABLE_LIMIT) return;
                }

                if (pendingFrameId) window.cancelAnimationFrame(pendingFrameId);
                pendingFrameId = window.requestAnimationFrame(() => {
                    pendingFrameId = null;
                    applyDimensions();
                });
            });
            resizeObserver.observe(iframeContainerRef.current);
        }

        return () => {
            teardown = true;
            if (firstFrameId) window.cancelAnimationFrame(firstFrameId);
            if (pendingFrameId) window.cancelAnimationFrame(pendingFrameId);
            if (resizeObserver) resizeObserver.disconnect();
            imageCleanupFns.forEach((fn) => fn());
            iframe.remove();
            isIframeAttached.current = false;
        };
    }, [isHtml, cleanHtml, containerId, id]);

    if (!id) {
        console.error('EmailIframe needs an id as prop');
        return null;
    }

    return (
        <Fragment>
            {!isHtml && <TextWithLinks text={html} />}
            {isHtml && (
                <div
                    id={containerId}
                    className="fm-email-iframe-container"
                    ref={iframeContainerRef}
                />
            )}
        </Fragment>
    );
});

export default EmailIframe;
